import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useLocalePath } from '../layout/LanguageRoute'
import SearchAutocompleteBox from '../product/SearchAutocompleteBox'
import type { Suggestion } from '../product/SearchAutocompleteBox'
import { fetchTotalProductCount } from '../../api/products'

/**
 * Homepage hero (homepage-v2, T3.2 §6.1.1). The hero *is* the search bar:
 * for the primary persona (an engineer with a type number) this single field
 * is the whole shop. Submit → product listing filtered by the term; picking a
 * suggestion jumps straight to that product. Autofocus is desktop-only so we
 * never pop the mobile keyboard over the fold.
 */
export default function HeroSearch() {
  const { t } = useTranslation()
  const lp = useLocalePath()
  const navigate = useNavigate()
  const [value, setValue] = useState('')

  // Desktop-only autofocus. window is undefined during SSR, so this stays false
  // on the server and the effect in SearchAutocompleteBox re-evaluates on the client.
  const autoFocusDesktop =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(min-width: 1024px)').matches

  const { data: totalCount } = useQuery({
    queryKey: ['total-product-count'],
    queryFn: fetchTotalProductCount,
    staleTime: 5 * 60 * 1000,
  })
  const skuDisplay = totalCount != null ? Math.floor(totalCount / 100) * 100 : null

  function submit(v: string) {
    const q = v.trim()
    navigate(q ? `${lp('/products')}?search=${encodeURIComponent(q)}` : lp('/products'))
  }

  function selectSuggestion(s: Suggestion) {
    navigate(lp(`/product/${s.slug}`))
  }

  return (
    <section className="bg-white border-b border-gcg-border">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
        <h1 className="text-[30px] sm:text-[38px] lg:text-[44px] font-bold text-gcg-text tracking-[-0.02em] leading-[1.1] mb-4">
          {t('shop.homeV2.hero.title')}
        </h1>
        <p className="text-[15px] sm:text-base text-neutral-600 leading-relaxed mb-8 max-w-[520px] mx-auto">
          {t('shop.homeV2.hero.subtitle')}
        </p>

        <form
          onSubmit={e => { e.preventDefault(); submit(value) }}
          className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-3"
          role="search"
        >
          <div className="flex-1">
            <SearchAutocompleteBox
              value={value}
              onChange={setValue}
              onSubmit={submit}
              onSelectSuggestion={selectSuggestion}
              autoFocus={autoFocusDesktop}
              placeholder={t('shop.homeV2.hero.placeholder')}
              inputClassName="w-full pl-11 pr-9 py-4 text-base rounded-md border border-neutral-300 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center px-7 py-4 bg-primary text-white text-base font-semibold rounded-md hover:bg-primary-hover transition-colors shrink-0"
          >
            {t('shop.homeV2.hero.searchButton')}
          </button>
        </form>

        {skuDisplay != null && (
          <p className="mt-6 text-xs font-medium text-neutral-500">
            {t('shop.homeV2.hero.meta', { count: skuDisplay })}
          </p>
        )}
      </div>
    </section>
  )
}
